import { api } from "@/api/client";

/** Одна торговая единица: что посчитали, что на площадке, что отправили. */
export interface StockSyncRow {
  marketplace: string;
  warehouse_key: string;
  /** Кабинет, склад маркетплейса и склад 1С; null — склад не найден среди складов
   *  активных кабинетов или указан у нескольких (причина — `warehouse_note`). */
  cabinet_name: string | null;
  warehouse_name: string | null;
  one_c_warehouse_name: string | null;
  warehouse_note: string | null;
  unit_key: string;
  article: string;
  computed_qty: number;
  platform_qty: number | null;
  published_qty: number | null;
  published_at: string | null;
  /** Почему не опубликовано: unknown_sku / ambiguous / over_delta / over_limit. */
  skip_reason: string | null;
  /** На сколько остаток на маркетплейсе превышает расчётный. */
  over: number;
}

export interface StockSyncRun {
  started_at: string;
  finished_at: string | null;
  dry_run: boolean;
  positions: number;
  changed: number;
  published: number;
  skipped: number;
  error: string | null;
  /** Замечания удачного прогона. Отдельно от `error`: раньше они ехали в ту же
   *  колонку, и успешный цикл с неоднозначной парой выглядел провалившимся. */
  notes: string | null;
}

export interface StockSyncStatus {
  /**
   * Самих настроек здесь НЕТ: они приезжают в `SystemSettingsView`, и вторая
   * их копия разъехалась бы — запросы инвалидируются раздельно.
   */
  one_c_ready: boolean;
  one_c_error: string | null;
  last_runs: StockSyncRun[];
  /** Начало последнего прогона; null — прогонов не было. `rows` — состояние на него. */
  last_run_at: string | null;
  /** Включённых складов на включённых маркетплейсах; 0 — сверять нечего. */
  enabled_warehouses: number;
  /** Только включённые склады и отмеченные товары; превышение сверху, ≤ 500 строк. */
  rows: StockSyncRow[];
  rows_total: number;
  /** Сумма превышений и число позиций с превышением — по всей выборке. */
  over_total: number;
  over_positions: number;
}

export async function status(): Promise<StockSyncStatus> {
  const { data } = await api.get<StockSyncStatus>("/stock-sync/status");
  return data;
}

/** Прогнать цикл сейчас. Режим (сухой или боевой) берётся из настроек. */
export async function runNow(): Promise<StockSyncStatus> {
  const { data } = await api.post<StockSyncStatus>("/stock-sync/run");
  return data;
}

// --- Предпросмотр расчёта склада (`GET /stock-sync/warehouses/{mp}/{wh}/preview`) ---

/** Спрос юрлица по видам. */
export interface StockSyncDemand {
  in_flight: number;
  wb: number;
  ozon: number;
  total: number;
}

/** Из чего складывается доля кабинета (`compute.Breakdown`). */
export interface StockSyncBreakdown {
  org: string;
  owner: string;
  is_owner: boolean;
  /** O — свободный остаток владельца. */
  owner_stock: number;
  /** S_org — остаток юрлица кабинета; у владельца null. */
  seller_stock: number | null;
  /** Спрос по видам; ключи — `org` и `owner`. */
  demand_by_org: Record<string, StockSyncDemand>;
  /** min(S_org, D_org); у владельца null. */
  seller_use: number | null;
  /** Спрос продавцов сверх их остатка — везёт владелец. */
  spill_total: number;
  /** R — остаток общего пула (бывает отрицательным). */
  pool_rest: number;
  /** Итог формулы = publish_for. */
  result: number;
}

export type StockSyncPreviewStatus =
  | "publish"
  | "zero_unselected"
  | "zero_unidentified"
  | "ambiguous"
  | "missing";

export interface StockSyncPreviewRow {
  unit_key: string;
  article: string;
  nomenclature_ref: string | null;
  characteristic_ref: string | null;
  name_1c: string | null;
  selected: boolean;
  status: StockSyncPreviewStatus;
  status_text: string;
  /** null — неопознанная / неоднозначная / пропавшая единица. */
  breakdown: StockSyncBreakdown | null;
  /** Сколько поставит прогон (с учётом выбора, до порога дельты); null — не трогает. */
  will_publish: number | null;
  /** Что прогон сделал бы сейчас: over_delta / over_limit / unknown_sku; null — публикует. */
  decision_skip: string | null;
  max_delta_hold: string | null;
  /** null — площадка не прочитана (см. `platform_error`). */
  platform_qty: number | null;
  last_computed_qty: number | null;
  last_published_qty: number | null;
  last_published_at: string | null;
  skip_reason: string | null;
}

export interface StockSyncPreview {
  computed_at: string;
  marketplace: string;
  cabinet_id: number;
  cabinet_name: string;
  warehouse_key: string;
  warehouse_name: string;
  one_c_warehouse_name: string | null;
  enabled: boolean;
  platform_enabled: boolean;
  dry_run: boolean;
  max_delta: number;
  last_run_at: string | null;
  last_run_error: string | null;
  /** Не хранится нигде — всегда null; спрос взят из базы как есть. */
  orders_polled_at: string | null;
  /** Отказ чтения площадки: расчёт показан, `platform_qty` пусты. */
  platform_error: string | null;
  rows: StockSyncPreviewRow[];
  notes: string[];
}

/** Живой расчёт склада без записи. */
export async function warehousePreview(
  marketplace: string,
  warehouseKey: string,
): Promise<StockSyncPreview> {
  const { data } = await api.get<StockSyncPreview>(
    `${warehousePath(marketplace, warehouseKey)}/preview`,
  );
  return data;
}

// --- Склады площадок в синке: включатель и выбор единиц -------------------------

/** Склад площадки в синке (`WarehouseRow`). */
export interface StockSyncWarehouse {
  marketplace: string;
  cabinet_id: number;
  cabinet_name: string;
  warehouse_key: string;
  warehouse_name: string;
  /** Склад 1С (комната) — именем, как сохранили в кабинете. */
  one_c_warehouse_name: string | null;
  enabled: boolean;
  selected_count: number;
  /** Однозначно сопоставленных единиц у кабинета; null — цели в этом процессе
   *  бэкенда ещё не читались (список в площадки не ходит). */
  units_count: number | null;
}

export interface StockSyncWarehouses {
  warehouses: StockSyncWarehouse[];
  /** Кабинеты, чьи склады в синк не попали (нет склада 1С и т.п.). */
  notes: string[];
}

function warehousePath(marketplace: string, warehouseKey: string): string {
  return `/stock-sync/warehouses/${encodeURIComponent(marketplace)}/${encodeURIComponent(warehouseKey)}`;
}

/** Список складов площадок всех комнат. Ни площадки, ни 1С не читает. */
export async function warehouses(): Promise<StockSyncWarehouses> {
  const { data } = await api.get<StockSyncWarehouses>("/stock-sync/warehouses");
  return data;
}

/** Включить/выключить склад. Включение с пустым выбором — 409 с текстом. */
export async function switchWarehouse(
  marketplace: string,
  warehouseKey: string,
  enabled: boolean,
): Promise<StockSyncWarehouse> {
  const { data } = await api.put<StockSyncWarehouse>(warehousePath(marketplace, warehouseKey), {
    enabled,
  });
  return data;
}

/** Полная замена выбора склада: 422 — неизвестные/неопознанные единицы
 *  (перечнем), 409 — пустой выбор у включённого склада. */
export async function saveSelection(
  marketplace: string,
  warehouseKey: string,
  unitKeys: string[],
): Promise<StockSyncWarehouse> {
  const { data } = await api.put<StockSyncWarehouse>(
    `${warehousePath(marketplace, warehouseKey)}/selection`,
    { unit_keys: unitKeys },
  );
  return data;
}

/** «Отметить все сопоставленные» — замена выбора всеми однозначными единицами. */
export async function selectAllMapped(
  marketplace: string,
  warehouseKey: string,
): Promise<StockSyncWarehouse> {
  const { data } = await api.post<StockSyncWarehouse>(
    `${warehousePath(marketplace, warehouseKey)}/selection/all-mapped`,
  );
  return data;
}

// --- Служебное подключение 1С (`/stock-sync/service-connection`) ---

/** Источник служебного подключения — по профилю инсталляции: `infisical`
 *  (правится в Infisical, поля пусты) или `db` (правится в портале). */
export type ServiceConnectionSource = "infisical" | "db";

export interface ServiceConnection {
  source: ServiceConnectionSource;
  base_url: string | null;
  username: string | null;
  /** Пароль не отдаётся никогда — только признак, что он задан. */
  password_set: boolean;
}

export interface ServiceConnectionUpdate {
  base_url: string;
  username: string;
  /** Пусто — «не менять» (если пароль уже задан). */
  password?: string;
}

export interface ServiceConnectionTest {
  ok: boolean;
  detail: string;
  status_code: number | null;
}

export async function serviceConnection(): Promise<ServiceConnection> {
  const { data } = await api.get<ServiceConnection>("/stock-sync/service-connection");
  return data;
}

export async function saveServiceConnection(
  body: ServiceConnectionUpdate,
): Promise<ServiceConnection> {
  const { data } = await api.put<ServiceConnection>("/stock-sync/service-connection", body);
  return data;
}

/** Пробный запрос к 1С с сохранёнными данными. 409 — запись не заполнена. */
export async function testServiceConnection(): Promise<ServiceConnectionTest> {
  const { data } = await api.post<ServiceConnectionTest>("/stock-sync/service-connection/test");
  return data;
}
